import React, { useState } from 'react';
import { DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  rectSwappingStrategy,
  SortingStrategy,
} from '@dnd-kit/sortable';

export { horizontalListSortingStrategy, rectSortingStrategy, rectSwappingStrategy, verticalListSortingStrategy, arrayMove };

type BaseItem = { id: string };

interface SortableContextProviderProps<TItem extends BaseItem> {
  children: React.ReactNode;

  items: TItem[];
  strategy?: SortingStrategy;
  onSort?: (from: number, to: number) => void;
  renderOverlay?: (item: TItem) => React.ReactNode;
}

const SortableContextProvider = <TItem extends BaseItem>(props: SortableContextProviderProps<TItem>) => {
  const { items, children, strategy = verticalListSortingStrategy, onSort, renderOverlay } = props;
  const [activeItem, setActiveItem] = useState<TItem | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragStart(event: DragEndEvent) {
    const { active } = event;
    const item = items.find((item) => item.id === active.id);
    if (item) {
      setActiveItem(item);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!onSort) {
      return;
    }
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      onSort(oldIndex, newIndex);
      setActiveItem(null);
    }
  }

  if (!onSort) {
    return <>{children}</>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={strategy}>
        {children}
      </SortableContext>
      {renderOverlay && <DragOverlay>{activeItem ? renderOverlay(activeItem) : null}</DragOverlay>}
    </DndContext>
  );
};

export default SortableContextProvider;
